import ContentCopy from "@mui/icons-material/ContentCopy";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Edit from "@mui/icons-material/Edit";
import FileDownload from "@mui/icons-material/FileDownload";
import LinkOutlined from "@mui/icons-material/LinkOutlined";
import {
  Alert,
  Button,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import { isEqual } from "lodash-es";
import { type FC, useCallback, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDialog } from "../../utils/hooks/use-dialog";
import { usePopover } from "../../utils/hooks/use-popover";
import { useDesignHuddleProjectClone } from "../design-huddle/use-design-huddle-project-clone";
import { ExportMenu } from "./asset-export-menu";
import { DeleteConfirmation } from "./asset-preview-modal";
import { AssetPreviewModalTitle } from "./asset-preview-modal-title";
import { AssetTagManager } from "./asset-tag-manager";
import { VideoAssetPreview } from "./components/cloudflare-stream-iframe";
import { useAvailableTags } from "./hooks/use-available-tags";
import { useDeleteAssetCallback } from "./hooks/use-delete-asset-callback";
import { useCreateAssetCallback } from "./hooks/use-upsert-asset";
import type { Asset } from "./server/digital-asset-service";

export const DesignHuddleAssetPreviewModal: FC<{
  open: boolean;
  onClose: () => void;
  asset: Asset;
  onChange?: () => void;
  onDeleted?: () => void;
}> = ({ asset, open, onClose, onChange, onDeleted }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [copying, setCopying] = useState(false);
  const createAsset = useCreateAssetCallback();

  const [filename, setFilename] = useState(asset.title);
  const [filenameUpdated, setFilenameUpdated] = useState<boolean>(false);
  const [localTags, setLocalTags] = useState<string[]>(asset?.tags || []);
  const { availableTags } = useAvailableTags();

  const handleCloseDialog = useCallback(() => {
    onClose();
    (filenameUpdated || !isEqual(localTags, asset?.tags ?? [])) && onChange?.();
    setFilenameUpdated(false);
  }, [asset?.tags, filenameUpdated, localTags, onChange, onClose]);

  const cloneProject = useDesignHuddleProjectClone();

  const handleCopy = useCallback(async () => {
    if (copying) return;

    setCopying(true);

    const loadingToast = toast.custom(<Alert severity="info">{t("Creating copy...")}</Alert>);

    if (!asset.project_id) return;
    try {
      const projectId = await cloneProject(asset.project_id, asset.page_id);
      await createAsset({
        projectId: projectId,
        contentType: asset.content_type,
        thumbnailUrl: asset.thumbnail_url,
        publicUrl: asset.public_url,
        pageId: asset.page_id,
        title: `Copy of ${asset.title}`,
        pageCount: asset.page_count,
      });

      toast.dismiss(loadingToast);
      toast.custom(<Alert severity="success">{t(`Copy of "${asset.title}" created`)}</Alert>, {
        duration: 2000,
      });
      onChange?.();
    } catch (e) {
      console.error(e);
      toast.dismiss(loadingToast);
      toast.custom(
        <Alert severity="error">{t("Failed to create copy. Please try again later")}</Alert>,
        { duration: 3000 },
      );
    } finally {
      setCopying(false);
    }
  }, [asset, cloneProject, copying, createAsset, onChange, t]);

  const deleteConfirmation = useDialog();
  const deleteAsset = useDeleteAssetCallback();

  const handleConfirmDelete = useCallback(async () => {
    deleteConfirmation.handleClose();

    try {
      await deleteAsset(asset.id);
      toast.custom(<Alert severity="success">{t(`"${asset.title}" deleted`)}</Alert>, {
        duration: 2000,
      });
      onDeleted?.();
    } catch (e) {
      console.error(e);
      toast.custom(
        <Alert severity="error">{t("Failed to delete asset. Please try again later")}</Alert>,
        {
          duration: 3000,
        },
      );
    }
  }, [asset, deleteAsset, deleteConfirmation, onDeleted, t]);

  const exportMenuController = usePopover<HTMLButtonElement>();

  const isVideo = asset.content_type === "Video";

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseDialog}
        slotProps={{ paper: { sx: { maxWidth: 650 } } }}
      >
        <DialogTitle display="flex" justifyContent="space-between" component="div">
          <AssetPreviewModalTitle
            asset={asset}
            filename={filename}
            setFilename={setFilename}
            setFilenameUpdated={setFilenameUpdated}
            handleCloseDialog={handleCloseDialog}
          />
        </DialogTitle>
        <DialogContent>
          <Stack display="flex" alignItems="center" justifyContent="center" mt={4} gap={2}>
            {isVideo ? (
              <VideoAssetPreview asset={asset} />
            ) : (
              asset.thumbnail_url && <CardMedia src={asset.thumbnail_url} component="img" />
            )}
          </Stack>
          <AssetTagManager
            asset={asset}
            localTags={localTags}
            setLocalTags={setLocalTags}
            availableTags={availableTags}
          />
        </DialogContent>
        <DialogActions sx={{ justifyItems: "right", px: 2, mb: 2 }}>
          <Button
            startIcon={<LinkOutlined />}
            data-analytics-id="content-library-preview-get-embed-link"
            onClick={() => {
              if (!asset.public_url) {
                toast.custom(<Alert severity="error">{t("Unable to get Link")}</Alert>, {
                  duration: 2000,
                });
                return;
              }

              void navigator.clipboard.writeText(asset.public_url);
              toast.custom(<Alert severity="success">{t("Link Copied to Clipboard")}</Alert>, {
                duration: 2000,
              });
            }}
          >
            {t("Get Embed Link")}
          </Button>
          <Button
            ref={exportMenuController.anchorRef}
            onClick={exportMenuController.handleOpen}
            startIcon={<FileDownload />}
            data-analytics-id="content-library-preview-export"
          >
            {t("Export")}
          </Button>
          <Button
            startIcon={<ContentCopy data-testid="ContentCopyIcon" />}
            data-analytics-id="content-library-preview-duplicate"
            onClick={() => void handleCopy()}
            loading={copying}
          >
            {t("Duplicate")}
          </Button>
          <Button
            startIcon={<Edit />}
            data-analytics-id="content-library-preview-edit"
            onClick={() => void navigate(`/content-library/${asset.id}`)}
          >
            {t("Edit")}
          </Button>
          <Button
            startIcon={<DeleteOutline data-testid="DeleteOutlineIcon" />}
            data-analytics-id="content-library-preview-delete"
            onClick={deleteConfirmation.handleOpen}
            color="error"
          >
            {t("Delete")}
          </Button>
        </DialogActions>
      </Dialog>
      <DeleteConfirmation
        asset={asset}
        open={deleteConfirmation.open}
        onClose={deleteConfirmation.handleClose}
        onConfirm={() => void handleConfirmDelete()}
      />
      <ExportMenu menuController={exportMenuController} asset={asset} />
    </>
  );
};
